/**
 * Customer Realtime Collaboration Hook
 * 
 * Provides customer data loading, realtime updates via Appwrite subscriptions,
 * and autosave functionality.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import appwrite from '../services/appwrite';
import { dataApi } from '../services/api';
import { usePresence } from './usePresence';

const { getCurrentUser, subscribeToDocument } = appwrite;

/**
 * Hook for collaboration on a specific customer record
 * 
 * @param {string} customerId - The customer ID to collaborate on
 * @param {object} initialData - Initial data for the customer (optional)
 * @returns {object} Collaboration state and methods
 */
export function useCustomerCollaboration(customerId, initialData = {}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});
  const {
    activeUsers,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = usePresence(customerId ? `customer:${customerId}` : null);

  // Initialize current user
  useEffect(() => {
    const initUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
    };
    initUser();
  }, []);

  // Load customer data from API
  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      return;
    }

    const loadCustomer = async () => {
      try {
        setLoading(true);
        const customerData = await dataApi.customerGet(customerId);
        setData(customerData);
      } catch (err) {
        console.error('Error loading customer:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [customerId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!customerId) return;

    const unsubscribe = subscribeToDocument('customers', customerId, (response) => {
      if (response.events?.includes('databases.*.collections.*.documents.*.update')) {
        // Update local state with server changes (unless we're the one who made the change)
        setData(prev => ({
          ...prev,
          ...response.payload
        }));
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [customerId]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Autosave function
  const saveChanges = useCallback(async () => {
    if (!customerId || Object.keys(pendingChangesRef.current).length === 0) return;

    const changesToSave = { ...pendingChangesRef.current };
    pendingChangesRef.current = {};

    try {
      setIsSyncing(true);
      await dataApi.customerUpdate(customerId, changesToSave);
    } catch (err) {
      console.error('Error saving customer changes:', err);
      // Re-add failed changes to pending
      pendingChangesRef.current = { ...pendingChangesRef.current, ...changesToSave };
    } finally {
      setIsSyncing(false);
    }
  }, [customerId]);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Save any pending changes before unmount
        saveChanges();
      }
    };
  }, [saveChanges]);

  // Update field with debounced autosave
  const updateField = useCallback(async (fieldName, value, debounceMs = 1000) => {
    // Update local state immediately
    setData(prev => ({ ...prev, [fieldName]: value }));

    // Track pending change
    pendingChangesRef.current[fieldName] = value;

    // Debounce save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveChanges, debounceMs);
  }, [saveChanges]);

  return {
    data,
    loading,
    activeUsers,
    currentUser,
    isOnline,
    isSyncing,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  };
}

